import React, { useState } from "react";
import axios from "axios";
import { getValidToken } from "../../utils/tokenManager";
import AdminSidebar from "components/common/AdminSidebar";
import Input from "../common/Input";
import Button from "../common/Button";
import Select from "../common/SelectInput";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import success from "../../assets/agent/success.svg";
import fail from "../../assets/agent/fail.svg";
import MobileNewUser from "./mobile/MobileNewUser";

const NewUser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const roles = [
    { value: "Admin", label: t("admin.option.admin") },
    { value: "Agent", label: t("admin.option.agent") },
  ];

  const workshifts = [
    { value: "Morning", label: t("admin.option.morning") },
    { value: "Afternoon", label: t("admin.option.afternoon") },
    { value: "Fulltime", label: t("admin.option.fulltime") },
  ];

  const initialFormState = {
    fullName: "",
    email: "",
    role: "",
    workshift: "",
    phone: "",
  };

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    if (!form.fullName.trim()) {
      newErrors.fullName = t("validation.requiredField");
    }

    if (!form.email.trim()) {
      newErrors.email = t("validation.requiredField");
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = t("validation.invalidEmail");
    }

    if (!form.role) {
      newErrors.role = t("validation.requiredRole");
    }

    if (!form.workshift) {
      newErrors.workshift = t("validation.requiredWorkshift");
    }

    if (!form.phone.trim()) {
      newErrors.phone = t("validation.requiredField");
    } else if (!phoneRegex.test(form.phone)) {
      newErrors.phone = t("validation.invalidPhone");
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrorModal(true);
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    const userData = {
      FullName: form.fullName,
      Email: form.email,
      Role: form.role,
      Workshift: form.workshift,
      Phone: form.phone,
    };
    const accessToken = await getValidToken();
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    try {
      const { status } = await axios.post(
        `${process.env.REACT_APP_APIBASEURL}/api/users`,
        userData,
        config,
      );

      if (status === 201) {
        setSuccessModal(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setSuccessModal(false);
    setForm(initialFormState);
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
    <div
      className="hidden lg:flex min-h-screen"
      style={{
        background:
          "linear-gradient(-80deg, #051D42 20%, #0C469F 82%, #0D4AA8 100%)",
      }}
    >
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col justify-center px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="w-full max-w-6xl mx-auto">
          <h1 className="text-[30px] font-bold text-white">
            {t("admin.newUser")}
          </h1>
          <p className="text-white mb-2 md:text-lg md:w-[735px]">
            {t("admin.description")}
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-white rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-white" />
            <span className="absolute right-0 w-1 h-1 bg-white rounded-full -mr-1"></span>
          </div>
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-6 mt-8 md:mt-[125px] rounded-xl"
            noValidate
          >
            <div>
              <Input
                label={t("admin.fullName")}
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                error={errors.fullName}
                autoComplete="name"
                required
              />
            </div>
            <div className="flex flex-wrap gap-x-5">
              <Select
                label={t("admin.role")}
                name="role"
                value={form.role}
                onChange={handleChange}
                error={errors.role}
                options={roles}
                required
                labelClassName="text-white"
              />
              <Select
                label={t("admin.workshift")}
                name="workshift"
                value={form.workshift}
                onChange={handleChange}
                error={errors.workshift}
                options={workshifts}
                required
                labelClassName="text-white"
              />
            </div>
            <div>
              <Input
                label={t("email")}
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                autoComplete="email"
                required
              />
            </div>
            <div>
              <Input
                label={t("admin.phone")}
                name="phone"
                value={form.phone}
                onChange={handleChange}
                error={errors.phone}
                autoComplete="tel"
                required
              />
            </div>
            <div className="md:col-span-2 flex justify-between items-center mt-4 md:mt-[150px]">
              <button
                type="button"
                className="text-white text-2xl font-semibold"
                onClick={handleBack}
              >
                {t("back")}
              </button>
              <Button
                variant="primary"
                type="submit"
                className="w-40"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("button.loading") : t("button.register")}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userConfirm.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl text-center w-[370px]">
              {t("admin.userConfirm.descUser")}
            </p>
            <p className="mb-6 text-xl text-secondary font-bold">
              {form.fullName}
            </p>
            <p className="mb-6 text-xl text-secondary">{form.email}</p>

            <Link to="/dashboard">
              <Button variant="primary" onClick={closeSuccessModal}>
                {t("admin.userConfirm.btn")}
              </Button>
            </Link>
          </div>
        </div>
      )}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
    <div className="lg:hidden">
      <MobileNewUser />
    </div>
    </>
  );
};

export default NewUser;
